import { useState, useEffect } from 'react';
import { offerStoreAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export function AddOfferModal({ open, onClose, onCreated }) {
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [vendorMode, setVendorMode] = useState('existing');
  const [form, setForm] = useState({
    model: '', description: '', price: '', qty: '', vendor_zoho_id: '', vendor_name: '',
    availability: 'READY_STOCK', lead_time_days: '', availability_note: '',
    offer_date: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    offerStoreAPI.getVendors().then(r => setVendors(r.data.vendors || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.model.trim()) { toast.error('Model is required'); return; }
    if (vendorMode === 'existing' && !form.vendor_zoho_id) { toast.error('Select a vendor'); return; }
    if (vendorMode === 'new' && !form.vendor_name.trim()) { toast.error('Vendor name is required'); return; }
    setSaving(true);
    try {
      await offerStoreAPI.create({
        model: form.model, description: form.description,
        price: form.price === '' ? null : Number(form.price), qty: form.qty === '' ? null : Number(form.qty),
        vendor_zoho_id: vendorMode === 'existing' ? form.vendor_zoho_id : null,
        vendor_name: vendorMode === 'new' ? form.vendor_name : '',
        availability: form.availability,
        lead_time_days: form.lead_time_days === '' ? null : Number(form.lead_time_days),
        availability_note: form.availability_note, offer_date: form.offer_date,
      });
      toast.success('Offer added');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add offer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="add-offer-modal">
        <DialogHeader><DialogTitle>Add offer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Model / description *</Label>
            <Input value={form.model} onChange={e => set('model', e.target.value)} className="rounded-sm mt-1" data-testid="add-offer-model-input" />
          </div>
          <div>
            <Label>Vendor *</Label>
            <Select value={vendorMode} onValueChange={setVendorMode}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="add-offer-vendor-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="existing">Pick an existing vendor</SelectItem>
                <SelectItem value="new">Type a new vendor name</SelectItem>
              </SelectContent>
            </Select>
            {vendorMode === 'existing' ? (
              <Select value={form.vendor_zoho_id} onValueChange={v => set('vendor_zoho_id', v)}>
                <SelectTrigger className="rounded-sm mt-2" data-testid="add-offer-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map(v => <SelectItem key={v.zoho_vendor_id} value={v.zoho_vendor_id}>{v.vendor_name}{v.country ? ` (${v.country})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={form.vendor_name} onChange={e => set('vendor_name', e.target.value)} placeholder="Vendor name" className="rounded-sm mt-2" data-testid="add-offer-vendor-name-input" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Price (USD)</Label><Input type="number" value={form.price} onChange={e => set('price', e.target.value)} className="rounded-sm mt-1" data-testid="add-offer-price-input" /></div>
            <div><Label>Qty</Label><Input type="number" value={form.qty} onChange={e => set('qty', e.target.value)} className="rounded-sm mt-1" data-testid="add-offer-qty-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Availability</Label>
              <Select value={form.availability} onValueChange={v => set('availability', v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="add-offer-availability-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="READY_STOCK">Ready stock</SelectItem>
                  <SelectItem value="LEAD_TIME">Lead time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.availability === 'LEAD_TIME' && (
              <div><Label>Lead time (days)</Label><Input type="number" value={form.lead_time_days} onChange={e => set('lead_time_days', e.target.value)} className="rounded-sm mt-1" data-testid="add-offer-lead-time-input" /></div>
            )}
          </div>
          <div><Label>Offer date</Label><Input type="date" value={form.offer_date} onChange={e => set('offer_date', e.target.value)} className="rounded-sm mt-1" data-testid="add-offer-date-input" /></div>
          <div><Label>Description / notes</Label><Textarea value={form.description} onChange={e => set('description', e.target.value)} className="rounded-sm mt-1" data-testid="add-offer-description-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="add-offer-cancel-btn">Cancel</Button>
          <Button className="rounded-sm" onClick={handleSubmit} disabled={saving} data-testid="add-offer-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Add offer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
